import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { useUsersSearch } from "./useUsersSearch";
import { MobileCreateUserSheet } from "./MobileUserDetailSheets";

function roleLabel(userItem) {
  const roles = [];
  if (userItem?.roles?.includes("admin")) roles.push("Admin");
  if (userItem?.roles?.includes("partner")) roles.push("Parceiro");
  return roles.length ? roles.join(" · ") : "Cliente";
}

export default function MobileUsersList({ user }) {
  const navigate = useNavigate();
  const { state } = useLocation() || {};
  const presetQ = state?.presetName || state?.presetEmail || "";
  const { users, loading, error, filters, setFilters, search, refresh } =
    useUsersSearch(user, presetQ ? { q: presetQ } : undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);

  useEffect(() => {
    search();
  }, [search]);

  const openFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setFiltersOpen(false);
    search(draftFilters);
  };

  const activeFilterCount =
    (filters.admin ? 1 : 0) +
    (filters.partner ? 1 : 0);

  const handleCreateSuccess = (userId) => {
    if (userId) {
      navigate(`/manafix/users/${userId}`);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
        >
          Cadastrar
        </button>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={filters.q}
        onChange={(q) => setFilters((current) => ({ ...current, q }))}
        onSubmit={(q) => {
          const next = { ...filters, q: typeof q === "string" ? q : "" };
          setFilters(next);
          search(next);
        }}
        placeholder="Nome, CPF, e-mail ou telefone"
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={openFilters}
          className="inline-flex min-h-[36px] items-center rounded-full border border-primary/50 bg-monao px-3 text-sm text-laranjinha/80 hover:bg-monao"
        >
          Filtros{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
        {filters.admin ? (
          <span className="inline-flex min-h-[36px] items-center rounded-full bg-primary/15 px-3 text-sm text-primary">
            Admin
          </span>
        ) : null}
        {filters.partner ? (
          <span className="inline-flex min-h-[36px] items-center rounded-full bg-primary/15 px-3 text-sm text-primary">
            Parceiro
          </span>
        ) : null}
      </div>

      {loading ? (
        <div aria-label="Carregando usuários" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar usuários."}
          onRetry={refresh}
        />
      ) : users.length === 0 ? (
        <MobileEmpty
          title="Nenhum usuário encontrado"
          message={
            filters.q?.trim()
              ? `Nenhum resultado para “${filters.q.trim()}”.`
              : "Ajuste os filtros ou tente atualizar a lista."
          }
        />
      ) : (
        <MobileList>
          {users.map((item) => (
            <MobileListItem
              key={item._id}
              title={item.name ?? "Sem nome"}
              subtitle={`${item.email ?? ""} · ${roleLabel(item)}`}
              onClick={() => navigate(`/manafix/users/${item._id}`)}
            />
          ))}
        </MobileList>
      )}

      <MobileFilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filtros"
      >
        <div className="space-y-4">
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-monao">
            <input
              type="checkbox"
              checked={draftFilters.admin}
              onChange={() =>
                setDraftFilters((current) => ({ ...current, admin: !current.admin }))
              }
              className="h-5 w-5 rounded border-primary/50"
            />
            <span className="text-sm text-laranjinha">Admin</span>
          </label>
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-monao">
            <input
              type="checkbox"
              checked={draftFilters.partner}
              onChange={() =>
                setDraftFilters((current) => ({
                  ...current,
                  partner: !current.partner,
                }))
              }
              className="h-5 w-5 rounded border-primary/50"
            />
            <span className="text-sm text-laranjinha">Parceiro</span>
          </label>
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 font-medium text-mgs-bg hover:bg-[#b47c06]"
          >
            Aplicar filtros
          </button>
        </div>
      </MobileFilterSheet>

      <MobileCreateUserSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        user={user}
        onCreated={handleCreateSuccess}
      />
    </div>
  );
}
